/**
 * @FIXME(shawk):
 * Until an upstream dependency issue in "eslint-plugin-react" is resolved,
 * disabling eslint rule "react/prop-types" is the recommended approach
 * to resolve false positives in eslint.
 *
 * see:
 * - https://github.com/yannickcr/eslint-plugin-react/issues/3140
 * - https://github.com/redwoodjs/redwood/pull/3762
 */

import cx from 'classnames';
import React, { useRef, useEffect, useState } from 'react';
import composeRefs from '@seznam/compose-react-refs';
import { IconButton } from 'components/IconButton';
import { HelperText, FieldLabel } from 'components/Forms';
import { Icon } from 'components/Icon';
import { ConnectedFormInputProps } from 'types/form-field';

export type Props = ConnectedFormInputProps<HTMLTextAreaElement> & {
  /** @note(juan): workaround to use this component in a controlled manner, will disable things like erase all button */
  isControlled?: boolean;
};

/**
 * @NOTE(shawk) @COPY: this is mostly the same as TextInput, except it renders a
 * <textarea> and uses the HTMLTextAreaElement type. Perhaps we can unify the two
 * using a prop and generic type at a later date.
 */
export const TextAreaInput = React.forwardRef<HTMLTextAreaElement, Props>(function TextAreaInputWithRef(
  {
    name,
    label,
    accessibleLabel,
    hint,
    error,
    disabled,
    className,
    labelClassName,
    setError,
    setValue,
    clearValue,
    isControlled,
    ...inputProps
  }: Props,
  ref?: React.ForwardedRef<HTMLTextAreaElement>,
): JSX.Element {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [isClearable, setIsClearable] = useState<boolean>(false);

  /**
   * @NOTE(shawk): We use this approach instead of mirroring input value in
   * react state since react-hook-form controls this input and it operates in
   * an uncontrolled fashion.
   */
  useEffect(() => {
    const el = inputRef.current;

    const onChange = (e: Event) => {
      const target = e.target as HTMLTextAreaElement;
      setIsClearable(!error && !!target.value);
    };

    el?.addEventListener('input', onChange);

    return () => {
      el?.removeEventListener('input', onChange);
    };
  }, [inputRef, error]);

  return (
    <div className={className}>
      <FieldLabel id={name} accessibleLabel={accessibleLabel} className={labelClassName}>
        {label}
      </FieldLabel>

      <div
        className={cx(
          'relative border flex ring-inset rounded-sm min-h-[48px] sm:min-h-[56px] transition-all font-dazzed',
          {
            'hover:bg-purple-50 bg-white hover:border-gray-400 border-black focus-within:ring-1 focus-within:border-black-900 focus-within:ring-black-900 border-black-300':
              !disabled,
            'text-gray-900': !disabled && !error,
            'bg-gray-100 text-gray-600 border-gray-400': disabled && !error,
            ' border-error-900': !!error,
          },
        )}
      >
        <div className="flex relative grow">
          <textarea
            {...inputProps}
            ref={composeRefs(inputRef, ref)}
            id={name}
            name={name}
            disabled={disabled}
            autoComplete="off"
            aria-invalid={error ? 'true' : 'false'}
            className={cx(
              'inset-0 w-full py-8 sm:py-12 pl-16 text-sm border-none bg-transparent leading-none outline-none placeholder:text-gray-600 min-h-[48px] sm:min-h-[56px]',
              {
                'pr-16': !error,
                'pr-48': !!error,
              },
            )}
          />

          {error && (
            <Icon
              name="Error"
              className="absolute top-0 bottom-0 right-0 mr-[13px] sm:mr-[15px] my-auto w-20 sm:w-24 text-error-900 pointer-events-none"
            />
          )}
        </div>

        {isClearable && !isControlled && (
          <IconButton
            size="sm"
            name="TimesCircle"
            variant="ghost"
            title="clear field"
            disabled={disabled}
            className="relative w-[34px] h-[34px] sm:w-[38px] sm:h-[38px] px-0 my-auto mx-6 sm:mx-8 min-h-0 flex-shrink-0 text-gray-600"
            iconClasses="h-20 w-20 sm:h-24 sm:w-24 text-gray-600"
            onClick={() => {
              if (clearValue) {
                clearValue();
                setIsClearable(false);
              }

              inputRef.current?.focus();
            }}
          />
        )}
      </div>

      {hint && <HelperText>{hint}</HelperText>}
      {error && <HelperText error>{error}</HelperText>}
    </div>
  );
});
